import { useTranslation } from "react-i18next";
import { bundleFormatColor } from "~/components/charts";
import { Text } from "~/components/text";
import { cn } from "~/utils";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from "~/utils/formatting";
import type { FormatMeta } from "~/pages/product-details/tabs/sales-data-tab/data/map-sales-response";
import type {
  AmazonFormatDatum,
  AmazonFormatDetail,
} from "~/pages/product-details/tabs/sales-data-tab/data/map-amazon-response";
import { FormatMatrixTooltip } from "~/pages/product-details/tabs/sales-data-tab/components/format-matrix-tooltip";

const DASH = "—";

/** Matrix tooltip for bundled (by-format) Amazon Sales: format columns with a
 *  row per metric (units, conversion, rank, sales). */
export const AmazonSalesFormatTooltip = ({
  formats,
  currency,
  active,
  payload,
}: {
  /** Bundle members, in the stack's display order (segment i = shade i). */
  formats: FormatMeta[];
  /** ISO 4217 currency for the sales row; null hides the amount. */
  currency: string | null;
  active?: boolean;
  payload?: { payload: AmazonFormatDatum }[];
}) => {
  const { t } = useTranslation(["productDetails"]);

  if (!active || !payload?.length || !formats.length) {
    return null;
  }
  const datum = payload[0].payload;

  const columns = formats.map((format, index) => ({
    key: format.isbn,
    label: format.label,
    color: bundleFormatColor(index),
  }));

  // Rank drops the per-format observation date the single-ISBN tooltip carries:
  // one shared row can't label five different best-rank dates.
  const rows: {
    label: string;
    bold?: boolean;
    cell: (detail: AmazonFormatDetail | undefined) => string;
  }[] = [
    {
      label: t("productDetails:salesData.amazonSales.tooltip.unitsBundle"),
      bold: true,
      cell: (detail) =>
        detail?.units == null ? DASH : formatNumber(detail.units),
    },
    {
      label: t("productDetails:salesData.amazonSales.tooltip.conversion"),
      cell: (detail) =>
        detail?.conversionRate == null
          ? DASH
          : formatPercent(detail.conversionRate),
    },
    {
      label: t("productDetails:salesData.amazonSales.tooltip.rank"),
      cell: (detail) =>
        detail?.salesRank == null ? DASH : `#${formatNumber(detail.salesRank)}`,
    },
    {
      label: t("productDetails:salesData.amazonSales.tooltip.salesBundle"),
      cell: (detail) =>
        detail?.revenue == null || !currency
          ? DASH
          : formatCurrency(detail.revenue, currency),
    },
  ];

  return (
    <FormatMatrixTooltip date={datum.date} columns={columns}>
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <Text as="span" variant="tooltip" className="text-white/60">
            {row.label}
          </Text>
          {formats.map((format) => (
            <Text
              key={format.isbn}
              as="span"
              variant="tooltip"
              numeric="mono"
              className={cn("text-right", row.bold && "font-semibold")}
            >
              {row.cell(datum.detail[format.isbn])}
            </Text>
          ))}
        </div>
      ))}
    </FormatMatrixTooltip>
  );
};
